<template>
  <section class="hero">
    <div class="hero-inner">
      <div class="hero-copy">
        <span class="hero-eyebrow">{{ $t("home.hero.eyebrow") }}</span>
        <h1 class="hero-headline u-display">
          {{ $t("home.hero.line1") }}<br />
          {{ $t("home.hero.line2") }}<br />
          <em>{{ $t("home.hero.line3") }}</em>
        </h1>
        <p class="hero-hook">{{ $t("home.hero.hook") }}</p>
        <div v-if="isNativeApp" class="hero-native-cta">
          <button type="button" @click="startAuth">
            {{ $t("home.hero.nativeCta") }}
          </button>
        </div>
        <template v-else>
          <StoreBadges class="hero-store-badges" />
          <button type="button" class="hero-web-link" @click="startAuth">
            {{ $t("home.hero.webLink") }}
          </button>
        </template>
        <p class="hero-proof">
          <!-- Drawn, not typed: Fraunces and Instrument Sans have no star
               codepoint, so a text star would fall back per-platform or tofu. -->
          <span class="hero-stars" aria-hidden="true">
            <svg v-for="n in 5" :key="n" viewBox="0 0 24 24" focusable="false">
              <path
                d="M12 2.6l2.83 6.12 6.67.72-4.98 4.5 1.38 6.56L12 17.1l-5.9 3.4 1.38-6.56-4.98-4.5 6.67-.72z"
              />
            </svg>
          </span>
          {{ $t("home.hero.proof") }}
        </p>
      </div>
      <div class="hero-visual">
        <div class="hero-callout hero-callout-memory">
          <!-- Drawn, not typed: an emoji here renders as a different picture on
               every OS and lands off-palette next to the tick. -->
          <span class="hero-callout-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path
                d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.06 4.94l-1.42 1.42A9 9 0 1 0 13 3zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8z"
              />
            </svg>
          </span>
          <span>
            <b>{{ $t("home.hero.calloutMemoryTitle") }}</b>
            {{ $t("home.hero.calloutMemoryBody") }}
          </span>
        </div>
        <PhoneChatMockup class="hero-phone" />
        <div class="hero-callout hero-callout-corrections">
          <span class="hero-callout-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </span>
          <span>
            <b>{{ $t("home.hero.calloutCorrectionsTitle") }}</b>
            {{ $t("home.hero.calloutCorrectionsBody") }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import StoreBadges from "@/components/StoreBadges.vue";
import PhoneChatMockup from "@/components/PhoneChatMockup.vue";
import { authUtils } from "@/scripts/authUtils.js";

export default {
  name: "HeroSection",
  components: { StoreBadges, PhoneChatMockup },
  data() {
    return {
      // Same detection StoreBadges uses: inside the Capacitor shells the
      // badges render nothing (Apple rejects builds that link to Google Play),
      // so the hero offers the sign-in CTA instead of an empty CTA area.
      isNativeApp: window.Capacitor?.isNativePlatform?.() === true,
    };
  },
  methods: {
    startAuth: authUtils.startAuth,
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this section has
   no dark-mode branch. */
.hero {
  background: linear-gradient(
    160deg,
    var(--al-white) 0%,
    var(--al-bg-light) 50%,
    var(--al-surface-muted) 100%
  );
  width: 100%;
  overflow: hidden;
}

.hero-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 64px 32px;
  display: flex;
  align-items: center;
  gap: 24px;
  position: relative;
}

/* Spec motion: one soft fade/rise on load, nothing else. */
@media (prefers-reduced-motion: no-preference) {
  .hero-inner {
    animation: heroEnter var(--al-dur-medium) var(--al-ease-enter);
  }
}

@keyframes heroEnter {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
}

.hero-inner::after {
  content: "";
  position: absolute;
  right: 0;
  top: 40px;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--al-primary) 16%, transparent),
    transparent 65%
  );
  pointer-events: none;
}

/* min-width: 0 lets the copy column shrink below its longest word instead of
   forcing the flex row wider than .hero-inner, which would clip the phone. */
.hero-copy {
  flex: 1;
  min-width: 0;
  max-width: 520px;
  z-index: 2;
}

/* Blue text on --al-primary-soft is a contrast trap: --al-primary is 2.9:1 and
   --al-primary-hover 3.8:1 against it, both under AA's 4.5:1 for 14px. Blue
   stays, one step deeper: --al-ink-600 is DESIGN.md's "rich blue accent" and
   lands at 6.2:1. Same reasoning drives .hero-web-link and the callout icons. */
.hero-eyebrow {
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  color: var(--al-ink-600);
  background: var(--al-primary-soft);
  border-radius: var(--al-radius-pill);
  padding: 6px 14px;
  margin-bottom: 16px;
}

.hero-headline {
  font-size: 44px;
  /* public/index.html loads Fraunces at discrete 600/700/800; 750 would be
     synthesized, so the display weight is 700. */
  font-weight: 700;
  line-height: 1.12;
  color: var(--al-ink-900);
  margin: 0 0 16px;
  text-align: left;
  overflow-wrap: break-word;
}

.hero-headline em {
  font-style: normal;
  color: var(--al-primary);
}

.hero-hook {
  font-size: 18px;
  line-height: 1.5;
  color: var(--al-gray);
  margin: 0 0 24px;
  text-align: left;
}

.hero-store-badges {
  align-items: flex-start;
}

/* DESIGN.md fixes the primary button as solid --al-primary with white text,
   which is 3.33:1 — fine for WCAG large text (>=18.66px bold) and short of AA
   for anything smaller. The plan's 18px sat just under that line, so the label
   is 19px: same button, same token, now on the right side of the threshold. */
.hero-native-cta button {
  font-size: 19px;
  font-weight: 700;
  font-family: inherit;
  color: var(--al-white);
  background: var(--al-primary);
  border: none;
  border-radius: var(--al-radius-md);
  padding: 14px 32px;
  min-height: 48px;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move);
}

.hero-native-cta button:hover,
.hero-native-cta button:active {
  background: var(--al-primary-hover);
}

/* Focus ring recipe from GeoMismatchModal.vue, in --al-ink-600 rather than
   --al-primary: the same ring has to read against the primary-blue button fill
   AND the pale hero gradient behind the text link. */
.hero-native-cta button:focus-visible,
.hero-web-link:focus-visible {
  outline: 3px solid var(--al-ink-600);
  outline-offset: 2px;
}

/* 14px on the gradient's darkest stop (--al-surface-muted): --al-primary-hover
   would be 4.21:1 there, --al-ink-600 is 6.8:1 and still reads as a blue link. */
.hero-web-link {
  display: inline-block;
  margin-top: 12px;
  font-size: 14px;
  font-family: inherit;
  color: var(--al-ink-600);
  background: none;
  border: none;
  padding: 4px 0;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.hero-proof {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 0;
  font-size: 14px;
  color: var(--al-gray-700);
}

.hero-stars {
  display: inline-flex;
  gap: 2px;
  flex-shrink: 0;
}

.hero-stars svg {
  width: 14px;
  height: 14px;
  fill: var(--al-amber);
  display: block;
}

/* Reserved lane for the callouts so they never cover the copy or the phone.
   The clearance between the copy column and a callout is exactly
   (flex gap 24) + (this margin) - (the callout's negative `left`), independent
   of viewport width, so 220 - 214 buys a 30px gutter at every desktop size.
   The tablet block below re-derives the same 24px gutter from its own numbers. */
.hero-visual {
  position: relative;
  flex-shrink: 0;
  z-index: 2;
  margin-left: 220px;
  margin-right: 12px;
}

.hero-callout {
  position: absolute;
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-md);
  box-shadow: var(--al-shadow-md);
  padding: 10px 14px;
  font-size: 12px;
  color: var(--al-gray-700);
  display: flex;
  gap: 8px;
  align-items: center;
  z-index: 4;
  width: 196px;
  text-align: left;
}

.hero-callout b {
  display: block;
  font-size: 12.5px;
  color: var(--al-ink-900);
}

.hero-callout-icon {
  width: 24px;
  height: 24px;
  border-radius: var(--al-radius-sm);
  background: var(--al-primary-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hero-callout-icon svg {
  width: 14px;
  height: 14px;
  fill: var(--al-ink-600);
  display: block;
}

.hero-callout-memory {
  top: 84px;
  left: -214px;
}

.hero-callout-corrections {
  bottom: 150px;
  left: -214px;
}

.hero-callout-corrections .hero-callout-icon {
  background: color-mix(in srgb, var(--al-success) 15%, var(--al-white));
}

.hero-callout-corrections .hero-callout-icon svg {
  fill: var(--al-success-dark);
}

/* Tablet, 900-1023px. The row is copy + gap + (callout lane + 250px phone) and
   the phone never shrinks, so every number below keeps the lane exactly as wide
   as the callouts plus a 12px gutter: at 168 - 156 the cards clear the bezel
   instead of covering the conversation they point at, and the copy column still
   gets ~410px. Below 900 the row runs out of room and the layout stacks. */
@media (max-width: 1023px) {
  .hero-inner {
    padding: 56px 24px;
  }

  .hero-headline {
    font-size: 34px;
  }

  .hero-hook {
    font-size: 16px;
  }

  .hero-visual {
    margin-left: 168px;
    margin-right: 0;
  }

  .hero-callout {
    width: 156px;
    font-size: 11.5px;
  }

  .hero-callout-memory {
    top: 48px;
    left: -168px;
  }

  .hero-callout-corrections {
    bottom: 116px;
    left: -168px;
  }
}

/* Stacked layout. The breakpoint is 899px rather than the 767px the rest of
   this page uses because the hero is the only section with a fixed-width
   250px child: side by side below 900px the copy column drops under ~280px,
   the headline breaks into six lines and the store badges stack. */
@media (max-width: 899px) {
  .hero-inner {
    flex-direction: column;
    padding: 40px 16px 48px;
    gap: 28px;
  }

  .hero-copy {
    max-width: 100%;
    text-align: center;
  }

  .hero-headline {
    font-size: 30px;
    text-align: center;
  }

  .hero-hook {
    font-size: 16px;
    text-align: center;
  }

  .hero-store-badges {
    align-items: center;
  }

  .hero-proof {
    justify-content: center;
  }

  .hero-visual {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  /* Chips, not floating cards: static flow under the phone. */
  .hero-callout {
    position: static;
    width: auto;
    max-width: 320px;
    font-size: 12px;
  }

  /* Chips read in order under the phone. */
  .hero-phone {
    order: 1;
  }

  .hero-callout-memory {
    order: 2;
  }

  .hero-callout-corrections {
    order: 3;
  }
}
</style>
